import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatET } from "../lib/dateUtils";

const navItems = [{ to: "/admin/lead-quality", label: "Lead Quality", icon: <MagnifyingGlass size={18} weight="duotone" />, testId: "nav-admin-lead-quality" }];

const NUMVERIFY_LABEL = { VALID: "VALID ✅", INVALID: "INVALID ❌", "NOT RUN": "NOT RUN —" };
const MILLIONVERIFIER_LABEL = { DELIVERABLE: "DELIVERABLE ✅", UNDELIVERABLE: "UNDELIVERABLE ❌", RISKY: "RISKY ⚠️", "NOT RUN": "NOT RUN —" };
const DNC_LABEL = { CLEAR: "CLEAR ✅", FLAGGED: "FLAGGED ❌", "NOT RUN": "NOT RUN —" };
const OVERALL_LABEL = { "FULLY VERIFIED": "FULLY VERIFIED ✅", PARTIAL: "PARTIAL ⚠️", FAILED: "FAILED ❌", "NOT RUN": "NOT RUN ❌" };
const OVERALL_COLOR = {
  "FULLY VERIFIED": "bg-igc-success/10 text-igc-success border-igc-success/30",
  PARTIAL: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  FAILED: "bg-igc-danger/10 text-igc-danger border-igc-danger/30",
  "NOT RUN": "bg-igc-muted/10 text-igc-muted border-igc-muted/30",
};

export default function AdminLeadQualityPage() {
  const [reps, setReps] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ repId: "all", leadType: "All", verificationStatus: "All", stage: "All", dateFrom: "", dateTo: "" });

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setReps(data.filter((u) => u.role === "affiliate" && u.account_status !== "revoked"))).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filters.repId !== "all") params.rep_id = filters.repId;
    if (filters.leadType !== "All") params.lead_type = filters.leadType.toLowerCase();
    if (filters.verificationStatus !== "All") params.verification_status = filters.verificationStatus;
    if (filters.stage !== "All") params.stage = filters.stage;
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    api.get("/admin/lead-quality", { params })
      .then(({ data }) => {
        setRows(data.rows);
        setSummary(data.summary);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [filters]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <DashboardLayout navItems={navItems} moduleName="Lead Quality" moduleBackPath="/admin">
      <div data-testid="admin-lead-quality-page">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="Every lead in the system, its verification results (phone, email, Do-Not-Call), and whether the rep has actually worked it.">Lead Quality View</h1>
        <p className="text-igc-muted text-sm mb-6">Every lead in the system, its verification results, and whether the rep has actually worked it. Admin-only — invisible to reps and managers.</p>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="lead-quality-summary-bar">
          {[
            ["Total (filtered)", summary?.total, "text-igc-goldText", "Total leads matching your current filters."],
            ["Fully Verified", summary?.fully_verified, "text-igc-success", "Leads that passed phone, email, and DNC verification."],
            ["Partial", summary?.partial, "text-amber-500", "Leads that passed some but not all verification checks."],
            ["Failed", summary?.failed, "text-igc-danger", "Leads that failed verification outright."],
            ["Not Run", summary?.not_run, "text-igc-muted", "Leads that haven't been through verification yet."],
            ["No Activity", summary?.no_activity, "text-igc-cream", "Leads assigned to a rep who hasn't logged any activity on them yet."],
          ].map(([label, value, color, hint]) => (
            <div key={label} className="bg-igc-surface border border-igc-gold/15 p-4 text-center" title={hint}>
              <p className={`text-2xl font-serif ${color}`} data-testid={`lead-quality-summary-${label.toLowerCase().replace(/\s/g, "-")}`}>{loading ? "—" : (value ?? 0)}</p>
              <p className="text-igc-muted text-[10px] uppercase tracking-widest mt-1">{label}</p>
            </div>
          ))}
        </div>

        <div className="bg-igc-surface border border-igc-gold/15 p-4 mb-4 flex flex-wrap gap-3 items-end" data-testid="lead-quality-filters">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-igc-muted mb-1 block">Rep</label>
            <Select value={filters.repId} onValueChange={(v) => setFilter("repId", v)}>
              <SelectTrigger data-testid="lead-quality-filter-rep" className="w-56 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream max-h-72">
                <SelectItem value="all">All Reps</SelectItem>
                {reps.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.display_name} ({r.user_id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-igc-muted mb-1 block">Lead Type</label>
            <Select value={filters.leadType} onValueChange={(v) => setFilter("leadType", v)}>
              <SelectTrigger data-testid="lead-quality-filter-lead-type" className="w-32 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="Trust">Trust</SelectItem>
                <SelectItem value="Funding">Funding</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-igc-muted mb-1 block">Verification Status</label>
            <Select value={filters.verificationStatus} onValueChange={(v) => setFilter("verificationStatus", v)}>
              <SelectTrigger data-testid="lead-quality-filter-verification" className="w-44 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="FULLY VERIFIED">Fully Verified</SelectItem>
                <SelectItem value="PARTIAL">Partial</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="NOT RUN">Not Run</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-igc-muted mb-1 block">Stage</label>
            <Select value={filters.stage} onValueChange={(v) => setFilter("stage", v)}>
              <SelectTrigger data-testid="lead-quality-filter-stage" className="w-36 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="Opened">Opened</SelectItem>
                <SelectItem value="Called">Called</SelectItem>
                <SelectItem value="Dispositioned">Dispositioned</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-igc-muted mb-1 block">Assigned From</label>
            <Input data-testid="lead-quality-filter-date-from" type="date" value={filters.dateFrom} onChange={(e) => setFilter("dateFrom", e.target.value)} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-40" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-igc-muted mb-1 block">Assigned To</label>
            <Input data-testid="lead-quality-filter-date-to" type="date" value={filters.dateTo} onChange={(e) => setFilter("dateTo", e.target.value)} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-40" />
          </div>
        </div>

        {loading && <p className="text-igc-muted text-sm" data-testid="lead-quality-loading">Loading leads...</p>}
        {!loading && rows.length === 0 && <p className="text-igc-muted text-sm" data-testid="lead-quality-empty-state">No leads match these filters.</p>}

        {!loading && rows.length > 0 && (
          <div className="bg-igc-surface border border-igc-gold/15 overflow-x-auto" data-testid="lead-quality-table-wrapper">
            <table className="w-full text-xs whitespace-nowrap" data-testid="lead-quality-table">
              <thead>
                <tr className="border-b border-igc-gold/15 text-igc-muted uppercase tracking-wide">
                  {["Rep Code", "Rep Name", "Lead ID", "Type", "First Name", "Last Name", "Phone", "State", "Date Assigned (ET)", "Stage", "Last Activity (ET)", "DataMoon", "NumVerify", "MillionVerifier", "DNC", "Verified On (ET)", "Overall"].map((h) => (
                    <th key={h} className="text-left px-3 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-igc-gold/10 text-igc-cream" data-testid={`lead-quality-row-${r.id}`}>
                    <td className="px-3 py-2">{r.rep_code || "—"}</td>
                    <td className="px-3 py-2">{r.rep_name}</td>
                    <td className="px-3 py-2 text-igc-muted font-mono">{r.id.slice(-8)}</td>
                    <td className="px-3 py-2">{r.lead_type}</td>
                    <td className="px-3 py-2">{r.first_name || "—"}</td>
                    <td className="px-3 py-2">{r.last_name || "—"}</td>
                    <td className="px-3 py-2 text-igc-muted">{r.phone || "—"}</td>
                    <td className="px-3 py-2 text-igc-muted">{r.state || "—"}</td>
                    <td className="px-3 py-2 text-igc-muted">{r.date_assigned ? formatET(r.date_assigned, "MMM d, yyyy h:mm a") : "—"}</td>
                    <td className="px-3 py-2">{r.current_stage}</td>
                    <td className="px-3 py-2 text-igc-muted">{r.last_activity ? formatET(r.last_activity, "MMM d, yyyy h:mm a") : "No activity"}</td>
                    <td className="px-3 py-2" data-testid={`lead-quality-datamoon-${r.id}`} title="Whether DataMoon successfully enriched this lead (identity/property/asset data attached) at intake.">
                      <span className={r.datamoon_status === "ENRICHED" ? "text-igc-success" : "text-igc-muted"}>{r.datamoon_status === "ENRICHED" ? "ENRICHED ✅" : "NO DATA —"}</span>
                    </td>
                    <td className="px-3 py-2">{NUMVERIFY_LABEL[r.numverify_result] || r.numverify_result}</td>
                    <td className="px-3 py-2">{MILLIONVERIFIER_LABEL[r.millionverifier_result] || r.millionverifier_result}</td>
                    <td className="px-3 py-2">{DNC_LABEL[r.dnc_result] || r.dnc_result}</td>
                    <td className="px-3 py-2 text-igc-muted">{r.verified_on ? formatET(r.verified_on, "MMM d, yyyy h:mm a") : "Not verified"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-1 uppercase tracking-wide border ${OVERALL_COLOR[r.overall_status]}`} data-testid={`lead-quality-overall-${r.id}`}>
                        {OVERALL_LABEL[r.overall_status] || r.overall_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
